import { Center, Spinner } from '@chakra-ui/react';
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';

import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import type { RevelioPageId } from '../CustomDash/components/PageConfigProvider/types';
import { GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT } from '../CustomDash/constants/panelWidths';
import { useApplyAiSearchFilters } from '../CustomDash/filters/ai-search/useApplyAiSearchFilters';
import { FilterProvider } from '../CustomDash/filters/contexts/FilterProvider';
import type { PrimaryFilterId } from '../CustomDash/filters/types/filters';
import type { GlobalAiChartPreviewRuntimeSnapshot } from './global-ai-assistant-chart-preview-runtime';
import { useGlobalAIAssistantOptional } from './global-ai-assistant-context';

function ApplyAiSearchFiltersOnce({
  messageId,
  filters,
  primarySelectionMergeMode,
  loadingMinHeight = GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT,
  children,
}: {
  messageId: string;
  filters: TalentDiscoveryAiFilterSearchResponseV2 | undefined;
  primarySelectionMergeMode: 'merge' | 'replace';
  loadingMinHeight?: string;
  children: ReactNode;
}) {
  const ga = useGlobalAIAssistantOptional();
  const mergeRuntimeRef = useRef<
    | ((
        id: string,
        patch: Partial<GlobalAiChartPreviewRuntimeSnapshot>
      ) => void)
    | undefined
  >(undefined);
  mergeRuntimeRef.current = ga?.mergeChartPreviewRuntime;
  const addTimelineEventRef = useRef<
    ((messageId: string, event: string) => void) | undefined
  >(undefined);
  addTimelineEventRef.current = ga?.addChartPreviewTimelineEvent;

  const { applyAiSearchFilters } = useApplyAiSearchFilters();
  const [filtersReady, setFiltersReady] = useState(filters === undefined);
  const applyRef = useRef(applyAiSearchFilters);
  applyRef.current = applyAiSearchFilters;

  const filtersKey =
    filters === undefined ? '__none__' : JSON.stringify(filters);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useLayoutEffect(() => {
    const filtersNow = filtersRef.current;
    const merge = mergeRuntimeRef.current;
    if (filtersNow === undefined) {
      merge?.(messageId, {
        phase: 'filters_ready',
        filterApplyError: undefined,
      });
      addTimelineEventRef.current?.(messageId, 'filters skipped (none)');
      setFiltersReady(true);
      return;
    }

    merge?.(messageId, {
      phase: 'applying_filters',
      filterApplyError: undefined,
    });
    addTimelineEventRef.current?.(messageId, 'applying filters');
    setFiltersReady(false);

    let cancelled = false;
    void (async () => {
      try {
        await applyRef.current(filtersNow, {
          primarySelectionMergeMode,
          skipTalentDiscoveryAdaptFallback: true,
        });
        if (!cancelled) {
          mergeRuntimeRef.current?.(messageId, {
            phase: 'filters_ready',
            filterApplyError: undefined,
          });
          addTimelineEventRef.current?.(messageId, 'ai filters applied');
        }
      } catch (e) {
        const msg =
          e instanceof Error ? e.message : 'Failed to apply AI filters';
        if (!cancelled) {
          mergeRuntimeRef.current?.(messageId, {
            phase: 'error',
            filterApplyError: msg,
          });
          addTimelineEventRef.current?.(messageId, `ai filters failed: ${msg}`);
        }
      } finally {
        if (!cancelled) {
          setFiltersReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [filtersKey, messageId, primarySelectionMergeMode]);

  if (!filtersReady) {
    return (
      <Center minH={loadingMinHeight} w="100%" flexShrink={0}>
        <Spinner color="green.500" />
      </Center>
    );
  }

  return children;
}

/**
 * Isolated filter state for a single global-assistant chart preview.
 * Does not read or write URL filters; applies the AI search snapshot once per message.
 */
export function GlobalAIAssistantChartFilterScope({
  messageId,
  filters,
  pageId,
  revelioPageId,
  primaryFilterId,
  primaryFilterSelectionLimit,
  primaryFilterSelectionDefault,
  primarySelectionMergeMode = 'replace',
  loadingMinHeight = GLOBAL_AI_INLINE_CHART_PLOT_HEIGHT,
  children,
}: {
  messageId: string;
  filters: TalentDiscoveryAiFilterSearchResponseV2 | undefined;
  pageId: string;
  revelioPageId?: RevelioPageId;
  primaryFilterId?: PrimaryFilterId;
  primaryFilterSelectionLimit?: number;
  primaryFilterSelectionDefault?: number;
  /**
   * `replace`: do not union AI primary entities with template defaults (single-bar previews).
   * @default 'replace'
   */
  primarySelectionMergeMode?: 'merge' | 'replace';
  loadingMinHeight?: string;
  children: ReactNode;
}) {
  return (
    <FilterProvider
      key={messageId}
      pageId={pageId}
      revelioPageId={revelioPageId}
      primaryFilterId={primaryFilterId}
      primaryFilterSelectionLimit={primaryFilterSelectionLimit}
      primaryFilterSelectionDefault={primaryFilterSelectionDefault}
      hydrateFromUrl={false}
      syncFiltersToUrl={false}
      persistScope={false}
    >
      <ApplyAiSearchFiltersOnce
        messageId={messageId}
        filters={filters}
        primarySelectionMergeMode={primarySelectionMergeMode}
        loadingMinHeight={loadingMinHeight}
      >
        {children}
      </ApplyAiSearchFiltersOnce>
    </FilterProvider>
  );
}
